import { SISU_REDIRECT_URL_SESSION_KEY } from '@tn/foundation/utils/constants/common';
import { getMarketingRegionIfEnabled } from '@tn/foundation/utils/server/region-utils';
import { and, AnyStateConfig, assertEvent, DoneActorEvent, enqueueActions, setup } from 'xstate';

import { FlowEntity, InitMachineParams } from '../../types';
import { CheckEmailFlowContext } from '../checkEmail';
import { getUserGuards, isStateGuard, refreshFullUserProfile } from '../helpers';
import { SignInFlowContext } from '../signin';
import { SignUpFlowContext } from '../signup';

import { clearEmailFromSession, persistEmailToSession, readEmailFromSession } from './emailSession';

// sessionStorage backup for the post-sign-in redirect destination. The redirectUrl arrives
// as a query param on the first SISU page (e.g. /account/check-email/?redirectUrl=...) and
// is normally persisted into machine context by `persistRedirectUrl`. We also stash it in
// sessionStorage so that:
//   * any timing edge case between Next.js router hydration and the xstate entry action
//     can't strand us with an empty `context.redirectUrl`, and
//   * subsequent in-flow URL pushes that don't carry the param can't lose it.
// Scope is the browser session, so cross-tab / cross-device leaks are impossible.
// The key lives in @tn/foundation so the Sign In CTA (which starts the flow) can clear a
// stale value when entering from the Front Door — see useRedirectToCheckEmail.

function persistRedirectUrlToSession(value: string | null | undefined): void {
    if (typeof window === 'undefined') return;
    try {
        if (typeof value === 'string' && value.length > 0) {
            window.sessionStorage.setItem(SISU_REDIRECT_URL_SESSION_KEY, value);
        }
    } catch {
        // sessionStorage may throw in private mode or when the quota is exceeded; swallow.
    }
}

function readRedirectUrlFromSession(): string | null {
    if (typeof window === 'undefined') return null;
    try {
        return window.sessionStorage.getItem(SISU_REDIRECT_URL_SESSION_KEY);
    } catch {
        return null;
    }
}

function clearRedirectUrlFromSession(): void {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.removeItem(SISU_REDIRECT_URL_SESSION_KEY);
    } catch {
        // Same rationale as above.
    }
}

/**
 * True if `value` is the Front Door (unauthenticated root `/` or `/{region}`).
 * * Prevents an auth loop where a signed-in user is bounced back to the landing page
 * instead of `/home`. Neutralizes hand-crafted or stale `redirectUrl=/` links.
 * * @param regionSegments Must include both marketing and raw region prefixes
 * (e.g., 'latam' and 'mx'), mirroring `getCanonicalRoute`. Trusted config only.
 */
export function isFrontDoorRedirect(
    value: string | null | undefined,
    regionSegments: string[],
): boolean {
    if (!value) return false;
    const path = value.split('#')[0].split('?')[0];
    let withoutRegion = path;
    for (const segment of regionSegments) {
        if (!segment) continue;
        const stripped = withoutRegion.replace(new RegExp(`^/${segment}(?=/|$)`, 'i'), '');
        if (stripped !== withoutRegion) {
            withoutRegion = stripped;
            break;
        }
    }
    return withoutRegion.replace(/\/+$/, '') === '';
}

export const ptvSisuFlow: FlowEntity = {
    initActor: (rawParams) => {
        const { config, actors, router, getConfigByKey, queryClient, region } =
            rawParams as InitMachineParams;
        const isState = isStateGuard({ getConfigByKey, flowId: 'ptvSisu', router, region });
        const userGuards = getUserGuards(queryClient);

        // The Front Door URL segment is the marketing region on Pluto TV
        // (mx→latam, de→gsa, gb→gb_ie), while `region` is the raw country code. Match
        // against both so isFrontDoorRedirect catches region-root targets in every
        // region. Mirrors getCanonicalRoute's duplicateRegionSegments: [marketing, raw].
        const allowMarketingRegionInPath = !!getConfigByKey('options.allowMarketingRegionInPath');
        const frontDoorRegionSegments = Array.from(
            new Set([getMarketingRegionIfEnabled(allowMarketingRegionInPath, region), region]),
        ).filter(Boolean);

        return setup({
            types: {
                context: {} as {
                    redirectUrl: string | null;
                    email: string;
                    skipOptInTermsPaModifiers: boolean;
                },
                events: {} as
                    | DoneActorEvent<CheckEmailFlowContext, 'checkEmail'>
                    | DoneActorEvent<SignInFlowContext, 'signIn'>
                    | DoneActorEvent<SignUpFlowContext, 'signUp'>
                    | { type: 'xstate.init' }
                    | { type: 'POP_STATE' }
                    | { type: 'ROUTE_CHANGE' }
                    | { type: 'SIGN_IN' }
                    | { type: 'SIGN_UP' }
                    | { type: 'ALREADY_LOGGED_IN' }
                    | { type: 'CHECK_EMAIL' },
            },
            guards: {
                isSignIn: and(isState('signIn', 'inclusion')),
                isSignUp: and(isState('signUp', 'inclusion')),
                isCheckEmail: and(isState('checkEmail', 'inclusion')),

                ...userGuards,
            },
            actions: {
                persistRedirectUrl: enqueueActions(({ enqueue }) => {
                    const redirectUrlFromQuery = router.query.redirectUrl;
                    const fromQuery = Array.isArray(redirectUrlFromQuery)
                        ? redirectUrlFromQuery[0]
                        : redirectUrlFromQuery;
                    // Prefer the value from the current URL; if it's missing (e.g. on a hard
                    // refresh of /account/sign-in mid-flow after the param was stripped from
                    // the URL by an intermediate router.push), fall back to whatever the first
                    // SISU entry captured into sessionStorage.
                    const candidate = fromQuery || readRedirectUrlFromSession();
                    // Drop Front Door / region-root targets so login doesn't
                    // bounce the user back to the landing page. Clear any stale root value
                    // from sessionStorage too, otherwise decideSignInDestination would read
                    // it straight back.
                    const redirectUrl = isFrontDoorRedirect(candidate, frontDoorRegionSegments)
                        ? null
                        : candidate;
                    enqueue.assign({ redirectUrl: redirectUrl || null });
                    if (redirectUrl) {
                        persistRedirectUrlToSession(redirectUrl);
                    } else {
                        clearRedirectUrlFromSession();
                    }
                }),
                persistEmail: enqueueActions(({ enqueue }) => {
                    // Rehydrate context.email from this tab's sessionStorage on a fresh
                    // render or hard refresh. sessionStorage is
                    // per-tab, so a concurrent submission in another tab can't bleed its
                    // email back into here.
                    const emailFromSession = readEmailFromSession();
                    if (typeof emailFromSession === 'string' && emailFromSession.length > 0) {
                        enqueue.assign({ email: emailFromSession });
                    }
                }),
                checkUserStatus: enqueueActions(({ enqueue, check }) => {
                    if (check({ type: 'isLoggedIn' })) {
                        enqueue.raise({ type: 'ALREADY_LOGGED_IN' });
                    }
                }),
                // Complements `clearEmailCheckpointOnNavigation`, which skips in-flow hops.
                clearEmailCheckpoint: enqueueActions(({ enqueue }) => {
                    enqueue.assign({ email: '' });
                    clearEmailFromSession();
                }),
                checkUserStatusEmail: enqueueActions(({ enqueue, check, context }) => {
                    if (check({ type: 'isLoggedIn' })) {
                        enqueue.raise({ type: 'ALREADY_LOGGED_IN' });
                    } else if (!context.email) {
                        enqueue.raise({ type: 'CHECK_EMAIL' });
                    }
                }),
                handleCheckEmailCompletion: enqueueActions(({ enqueue, event }) => {
                    assertEvent(event, 'xstate.done.actor.checkEmail');

                    enqueue.assign({ email: event.output.email });

                    // stash the email in this tab's sessionStorage so the
                    // sign-in/sign-up client guard can rehydrate it. Per-tab scope means a
                    // submission in another tab can't overwrite it, and the value never
                    // enters a cookie / request header.
                    persistEmailToSession(event.output.email);

                    switch (event.output.destinationState) {
                        case 'signIn':
                            router.push('/account/sign-in');
                            enqueue.raise({ type: 'SIGN_IN' });
                            break;
                        case 'signUp':
                            router.push('/account/sign-up');
                            enqueue.raise({ type: 'SIGN_UP' });
                            break;
                        default:
                            break;
                    }
                }),
                decideSignInDestination: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, ['xstate.done.actor.signIn', 'ALREADY_LOGGED_IN']);
                    clearEmailFromSession();
                    const navigate = () => {
                        const target = context.redirectUrl || readRedirectUrlFromSession();
                        clearRedirectUrlFromSession();
                        // Replace (not push) so /account/sign-in is not left in
                        // history — browser back from home would otherwise briefly show the
                        // password form while the flow re-redirects.
                        // A Front Door / region-root target must fall through to
                        // /home so the user isn't returned to the landing page.
                        if (target && !isFrontDoorRedirect(target, frontDoorRegionSegments)) {
                            router.replace(target);
                        } else {
                            router.replace('/home');
                        }
                    };
                    if (event.type === 'ALREADY_LOGGED_IN') {
                        enqueue(async () => {
                            await refreshFullUserProfile(queryClient);
                            navigate();
                        });
                    } else {
                        navigate();
                    }
                }),
                decideSignUpDestination: enqueueActions(({ enqueue, event, context }) => {
                    assertEvent(event, ['xstate.done.actor.signUp', 'ALREADY_LOGGED_IN']);
                    clearEmailFromSession();
                    const navigate = () => {
                        const target = context.redirectUrl || readRedirectUrlFromSession();
                        clearRedirectUrlFromSession();
                        // Replace (not push) so /account/sign-up is not left in
                        // history — browser back from home would otherwise briefly show the
                        // sign-up form while the flow re-redirects.
                        // A Front Door / region-root target must fall through to
                        // /home so the user isn't returned to the landing page.
                        if (target && !isFrontDoorRedirect(target, frontDoorRegionSegments)) {
                            router.replace(target);
                        } else {
                            router.replace('/home');
                        }
                    };
                    if (event.type === 'ALREADY_LOGGED_IN') {
                        enqueue(async () => {
                            await refreshFullUserProfile(queryClient);
                            navigate();
                        });
                    } else {
                        navigate();
                    }
                }),
            },
            actors,
        }).createMachine({
            ...(config as AnyStateConfig),
            context: ({ input }) => ({
                redirectUrl: null,
                email: '',
                skipOptInTermsPaModifiers: true,
                ...input,
            }),
        });
    },
};
